import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../../context/CartContext';
import './kauf.css';

// Meldung nach der Rückkehr aus der Stripe-Kasse. Das Backend (server.py, create-checkout und
// create-cart-checkout) schickt den Kunden zurück auf
//   Abo:        /shop/abo-jahr bzw. /shop/abo-monat  ?session_id=…&status=success  |  ?status=cancelled
//               (ProductDetail leitet das mit Query auf /shop/all-in-one weiter)
//   Warenkorb:  /shop?cart_success=true&session_id=…  |  /shop?cart_cancelled=true
//   Einzelkauf: /shop/<id>?session_id=…&status=success  |  ?status=cancelled
// Die Rückkehr-Adresse ist immer die deutsche. Darum merkt sich die Seite vor dem Sprung in die Kasse die
// Sprache (merkeKaufSprache) und springt bei der Rückkehr auf die /en-Fassung, wenn sie englisch war.
// Danach verschwindet die Query aus der Adresse, damit die Meldung beim Neuladen nicht wiederkommt.
//
// Was nach dem Kauf passiert (server.py, Webhook checkout.session.completed): Lizenzschlüssel anlegen, Kauf-Mail
// an die E-Mail-Adresse aus der Kasse mit Schlüssel und Installer-Links (Mac/Windows). Die Bibliothek im Konto
// (/api/me/library) sucht die Käufe über dieselbe E-Mail-Adresse. Beim Abo: 7 Tage Test, abgebucht wird danach.

const SPRACHE = 'davidmedia_kauf_sprache';
const PARAMETER = ['status', 'session_id', 'cart_success', 'cart_cancelled'];

// Vor dem Sprung in die Kasse aufrufen (All-in-One-Start, Warenkorb-Kasse)
export const merkeKaufSprache = (en) => {
  try { sessionStorage.setItem(SPRACHE, en ? 'en' : 'de'); } catch (e) { /* ohne Speicher bleibt es deutsch */ }
};

// 'en', 'de' oder null
export const gemerkteKaufSprache = () => {
  try { return sessionStorage.getItem(SPRACHE); } catch (e) { return null; }
};

const vergissKaufSprache = () => {
  try { sessionStorage.removeItem(SPRACHE); } catch (e) { /* nichts zu tun */ }
};

// Was die Adresse meldet: 'erfolg', 'abbruch' oder null
const fallAus = (art, search) => {
  const q = new URLSearchParams(search);
  if (art === 'korb') {
    if (q.get('cart_success') === 'true') return 'erfolg';
    if (q.get('cart_cancelled') === 'true') return 'abbruch';
    return null;
  }
  if (q.get('status') === 'success') return 'erfolg';
  if (q.get('status') === 'cancelled') return 'abbruch';
  return null;
};

// Dieselbe Seite in der anderen Sprache: alle Rückkehr-Seiten liegen unter /shop, englisch unter /en/shop
const pfadIn = (en, pfad) => {
  const de = pfad.replace(/^\/en(?=\/|$)/, '') || '/';
  return en ? '/en' + (de === '/' ? '' : de) : de;
};

const Zeichen = ({ erfolg }) => (
  <span className="kh-zeichen" aria-hidden="true">
    {erfolg
      ? <svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
      : <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8.5" /><path d="M12 11v5M12 8h.01" /></svg>}
  </span>
);

// art: 'abo' (All‑in‑One-Seite), 'korb' (/shop), 'einzel' (Produktseite, produktName für den Text)
export default function KaufHinweis({ art, en, produktName }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearCart, openCart } = useCart();
  // Einmal beim Laden lesen: danach räumt replaceState die Adresse auf
  const [fall] = useState(() => fallAus(art, location.search));
  const [offen, setOffen] = useState(false);
  const knopf = useRef(null);
  const T = (de, enText) => (en ? enText : de);
  const p = en ? '/en' : '';

  useEffect(() => {
    if (!fall) return;
    // Kasse wurde auf der anderen Sprache geöffnet: dorthin, Query mitnehmen
    const gemerkt = gemerkteKaufSprache();
    if (gemerkt && (gemerkt === 'en') !== en) {
      navigate(pfadIn(gemerkt === 'en', location.pathname) + location.search, { replace: true });
      return;
    }
    vergissKaufSprache();
    // Bezahlt: der Korb hat seinen Dienst getan. Abgebrochen: er bleibt, wie er war.
    if (art === 'korb' && fall === 'erfolg') clearCart();
    const q = new URLSearchParams(window.location.search);
    PARAMETER.forEach(n => q.delete(n));
    const rest = q.toString();
    window.history.replaceState(window.history.state, '', window.location.pathname + (rest ? '?' + rest : '') + window.location.hash);
    setOffen(true);
  }, []); // nur beim Laden: die Query gilt einmal, danach ist sie aus der Adresse

  // Esc schließt, der Hauptknopf bekommt den Fokus
  useEffect(() => {
    if (!offen) return undefined;
    const t = setTimeout(() => knopf.current && knopf.current.focus({ preventScroll: true }), 60);
    const taste = e => { if (e.key === 'Escape') setOffen(false); };
    window.addEventListener('keydown', taste);
    return () => { clearTimeout(t); window.removeEventListener('keydown', taste); };
  }, [offen]);

  if (!fall) return null;
  const erfolg = fall === 'erfolg';
  const zu = () => setOffen(false);
  const zumKorb = () => { setOffen(false); openCart(); };

  const mail = T('per E-Mail an die Adresse, die du in der Kasse angegeben hast', 'to the email address you entered at checkout');
  const inhalt = erfolg ? {
    kicker: art === 'abo' ? T('All‑in‑One · Abo gestartet', 'All‑in‑One · subscription started') : T('Bestellung abgeschlossen', 'Order complete'),
    titel: art === 'abo' ? T('Danke, dein All‑in‑One läuft.', 'Thanks, your All‑in‑One is active.') : T('Danke für deinen Kauf.', 'Thanks for your purchase.'),
    text: {
      abo: T(`Die ersten 7 Tage sind kostenlos, abgebucht wird erst danach. Deinen Lizenzschlüssel und die Links zum David Media Installer schicken wir dir ${mail}.`,
        `The first 7 days are free, you’re only charged after that. We’re sending your license key and the links to the David Media Installer ${mail}.`),
      korb: T(`Deine Lizenzschlüssel schicken wir dir ${mail}, zusammen mit den Links zum David Media Installer.`,
        `We’re sending your license keys ${mail}, together with the links to the David Media Installer.`),
      einzel: T(`Deinen Lizenzschlüssel für ${produktName} schicken wir dir ${mail}, zusammen mit den Links zum David Media Installer.`,
        `We’re sending your license key for ${produktName} ${mail}, together with the links to the David Media Installer.`),
    }[art],
    klein: T('Der Installer richtet alles ein und hält es aktuell. Mit einem Konto unter derselben E-Mail-Adresse findest du alles auch in deiner Bibliothek.',
      'The installer sets everything up and keeps it up to date. With an account under the same email address, you’ll also find everything in your library.'),
  } : {
    kicker: T('Kasse', 'Checkout'),
    titel: T('Kauf abgebrochen.', 'Purchase cancelled.'),
    text: T('Es wurde nichts abgebucht.', 'Nothing was charged.') + {
      abo: T(' Du kannst das All‑in‑One jederzeit neu starten.', ' You can start the All‑in‑One again any time.'),
      korb: T(' Dein Warenkorb ist noch da.', ' Your cart is still there.'),
      einzel: '',
    }[art],
  };

  return createPortal(
    <AnimatePresence>
      {offen && (
        <motion.div
          key="kauf-hinweis"
          className="kauf-schleier"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.25 } }}
          exit={{ opacity: 0, transition: { duration: 0.2 } }}
          onClick={e => { if (e.target === e.currentTarget) zu(); }}
          data-testid="kauf-hinweis"
          data-fall={fall}
        >
          <motion.div
            className="kauf-hinweis"
            role="dialog"
            aria-modal="true"
            aria-labelledby="kauf-hinweis-titel"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } }}
            exit={{ opacity: 0, y: 12, transition: { duration: 0.2 } }}
          >
            <div className="kh-glut" aria-hidden="true" />
            <button className="kh-zu" onClick={zu} aria-label={T('Schließen', 'Close')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
            <div className="kh-kopf"><Zeichen erfolg={erfolg} /><span className="kh-kicker">{inhalt.kicker}</span></div>
            <h2 id="kauf-hinweis-titel">{inhalt.titel}</h2>
            <p>{inhalt.text}</p>
            {inhalt.klein && <p className="kh-klein">{inhalt.klein}</p>}
            <div className="kh-knoepfe">
              {!erfolg && art === 'korb' ? (
                <>
                  <button ref={knopf} className="kh-btn primaer" onClick={zumKorb} data-testid="kauf-hinweis-korb">{T('Zum Warenkorb', 'Open cart')}</button>
                  <button className="kh-btn glas" onClick={zu}>{T('Schließen', 'Close')}</button>
                </>
              ) : (
                <>
                  <button ref={knopf} className="kh-btn primaer" onClick={zu} data-testid="kauf-hinweis-ok">{T('Alles klar', 'Got it')}</button>
                  {erfolg && <Link className="kh-btn glas" to={`${p}/installer`} onClick={zu}>{T('Zum Installer', 'Go to installer')}</Link>}
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
